import * as React from 'react'
import { useMemo } from 'react'
import { Button, Drawer, Well } from '@pushly/aqe/lib/components'
import { Form, notification, Select, Skeleton } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { simpleNotification } from '@halo/_utils/notifications'
import { simpleFormErrorNotification } from '@pushly/aqe/lib/utils'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import * as randomstring from 'randomstring'
import { DomainDto } from '@halo/dtos/domain.dto'
import { DomainService } from '@halo/services/domain.service'
import { IPageAnalysisConfig } from '@pushly/aqe/lib/interfaces'
import { EditOutlined } from '@ant-design/icons'

interface IDomainPageAnalysisConfigEditor {
    domain: DomainDto
    pageAnalysisConfigs: IPageAnalysisConfig[]
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDomainPageAnalysisConfigEditorFormValues {
    pageAnalysisConfig?: { key: string | null; label: React.ReactNode }
}

function getDomainPageAnalysisConfigName(domain: DomainDto, configs: IPageAnalysisConfig[]) {
    const configId = domain?.page_analysis_config_id
    let name = 'Default'

    if (configId) {
        const config = configs.find((pac) => pac.id === configId)
        if (config) {
            name = config.name
        }
    }

    return name
}

const computeChangedDomainValues = (
    domain: DomainDto,
    formValues: IDomainPageAnalysisConfigEditorFormValues,
): Partial<DomainDto> => {
    const values: Partial<DomainDto> = {}

    const selected = formValues.pageAnalysisConfig
    if (selected !== undefined) {
        const nextId = JSON.parse(selected.key?.toString() ?? 'null')
        if (nextId !== (domain.page_analysis_config_id ?? null)) {
            values.page_analysis_config_id = nextId
        }
    }

    return values
}

export const DomainPageAnalysisConfigEditor: React.FC<IDomainPageAnalysisConfigEditor> = (props) => {
    const appService = useMemo(() => Container.get(AppService), [])
    const domainService = useMemo(() => Container.get(DomainService), [])
    // Retain AppState container lookup parity with prior implementation.
    useMemo(() => Container.get(AppState), [])

    const { domain, pageAnalysisConfigs, visible, onClose, onDomainUpdated } = props

    const [form] = Form.useForm<IDomainPageAnalysisConfigEditorFormValues>()

    // Subscribe to value changes so the wrapping Drawer's disableSubmit recomputes as the user edits.
    Form.useWatch([], form)

    const currentValues = computeChangedDomainValues(domain, form.getFieldsValue())

    const handleSubmit = async () => {
        try {
            await form.validateFields()
            const values = form.getFieldsValue()
            const pageAnalysisConfigId = JSON.parse(values.pageAnalysisConfig?.key?.toString() ?? 'null')

            const { ok, data } = await domainService.patch(
                domain.id,
                {
                    pageAnalysisConfigId,
                },
                {
                    showLoadingScreen: true,
                },
            )

            if (ok) {
                onDomainUpdated(data)
                onClose(null)
            }
        } catch (err) {
            // v6 validateFields rejects with { errorFields, values, outOfDate };
            // aqe's simpleFormErrorNotification handles both legacy and v6 shapes.
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            getContainer={appService.getAppContainer}
            className={classnames('ddf-ecomm-settings-editor')}
            title="Update Page Analysis Config Settings"
            placement="right"
            closable={true}
            width={800}
            onSubmit={handleSubmit}
            disableSubmit={Object.keys(currentValues).length === 0}
            onClose={onClose}
            open={visible}
        >
            <Form
                form={form}
                onFinish={handleSubmit}
                size="small"
                labelAlign="left"
                requiredMark={false}
            >
                <Well mode="ghost" hideHeader={true} footerMode="hidden" padding="none">
                    <Form.Item
                        label="Page Analysis Config"
                        name="pageAnalysisConfig"
                        initialValue={{
                            key: domain.page_analysis_config_id,
                            label: getDomainPageAnalysisConfigName(domain, pageAnalysisConfigs),
                        }}
                    >
                        <Select labelInValue={true}>
                            <Select.Option key="null" value={null}>
                                Default
                            </Select.Option>

                            {pageAnalysisConfigs
                                .filter((pac) => !pac.is_default)
                                .map((d) => {
                                    return (
                                        <Select.Option key={d.id} value={d.id}>
                                            {d.name}
                                        </Select.Option>
                                    )
                                })}
                        </Select>
                    </Form.Item>
                </Well>
            </Form>
        </Drawer>
    )
}

interface IDomainPageAnalysisConfigWellProps {
    loading?: boolean
    domain: DomainDto
    pageAnalysisConfigs: IPageAnalysisConfig[]
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDomainPageAnalysisConfigWellState {
    showDrawer: boolean
    drawerKey?: string
}

export class DomainPageAnalysisConfigWell extends React.Component<
    IDomainPageAnalysisConfigWellProps,
    IDomainPageAnalysisConfigWellState
> {
    public state: IDomainPageAnalysisConfigWellState = {
        showDrawer: false,
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Page Analysis Config Settings"
                actions={
                    <Button
                        size="small"
                        onClick={this.handleDrawerOpen}
                        disabled={this.props.loading}
                        icon={<EditOutlined />}
                    >
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Well mode="ghost" hideHeader={true} hideFooter={true}>
                                <div className={classnames('form-layout-row')}>
                                    <div className={classnames('form-layout-row-label')}>Page Analysis Config</div>
                                    {getDomainPageAnalysisConfigName(this.domain, this.props.pageAnalysisConfigs)}
                                </div>
                            </Well>

                            <DomainPageAnalysisConfigEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                pageAnalysisConfigs={this.props.pageAnalysisConfigs}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
